import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Eye,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';

import { fetchPlayers } from './services/playersService';
import type {
  BalanceSortDirection,
  Player,
  PlayersPagination,
} from './types';

const PAGE_SIZE = 25;

const formatCurrency = (value: string | number | null | undefined) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const PlayersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [players, setPlayers] = useState<Player[]>([]);
  const [pagination, setPagination] = useState<PlayersPagination | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [balanceSort, setBalanceSort] =
    useState<BalanceSortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadPlayers = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetchPlayers({
        page: currentPage,
        page_size: PAGE_SIZE,
        search: searchTerm,
        balanceSort,
      });

      setPlayers(response.users);
      setPagination(response.pagination);
      setTotalCount(response.total_count);
      setFilteredCount(response.filtered_count);
    } catch (err: any) {
      setPlayers([]);
      setPagination(null);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to load players.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, balanceSort]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(1);
      setSearchTerm(searchInput.trim());
    }, 400);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const totalPages = useMemo(() => {
    if (filteredCount <= 0) return 1;
    return Math.max(1, Math.ceil(filteredCount / PAGE_SIZE));
  }, [filteredCount]);

  const handleBalanceSort = () => {
    setCurrentPage(1);
    setBalanceSort((current) => (current === 'desc' ? 'asc' : 'desc'));
  };

  const handleOpenPlayer = (player: Player) => {
    const playersBasePath = location.pathname.replace(/\/$/, '');

    navigate(`${playersBasePath}/${player.id}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Users className="h-6 w-6" />
                </div>

                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">
                    Players
                  </h1>
                  <p className="mt-1 text-sm text-slate-500">
                    Search players, review balances and open financial history.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={loadPlayers}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
              />
              Refresh
            </button>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-500">
                Total System Users
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {totalCount.toLocaleString('en-IN')}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-500">
                Matching Players
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {filteredCount.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label
              htmlFor="player-search"
              className="mb-2 block text-sm font-medium text-slate-700"
            >
              Search players
            </label>

            <div className="relative max-w-2xl">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />

              <input
                id="player-search"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by name, phone, email or referral code"
                className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {error && (
            <div className="border-b border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-[1050px] w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    ID
                  </th>

                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    User Name / Phone
                  </th>

                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Last Login
                  </th>

                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Phone Number
                  </th>

                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    First Name
                  </th>

                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <button
                      type="button"
                      onClick={handleBalanceSort}
                      className="inline-flex items-center gap-2 transition hover:text-slate-900"
                    >
                      Total Balance
                      {balanceSort === 'desc' ? (
                        <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUp className="h-4 w-4" />
                      )}
                    </button>
                  </th>

                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Status
                  </th>

                  <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-16 text-center text-sm text-slate-500"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        Loading players...
                      </div>
                    </td>
                  </tr>
                ) : players.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-16 text-center text-sm text-slate-500"
                    >
                      No players found.
                    </td>
                  </tr>
                ) : (
                  players.map((player) => {
                    const displayName =
                      player.full_name?.trim() ||
                      `${player.first_name || ''} ${player.last_name || ''}`.trim() ||
                      player.mobile_number;

                    return (
                      <tr
                        key={player.id}
                        className="transition hover:bg-slate-50"
                      >
                        <td className="whitespace-nowrap px-5 py-4 text-sm font-medium text-slate-900">
                          {player.id}
                        </td>

                        <td className="whitespace-nowrap px-5 py-4">
                          <button
                            type="button"
                            onClick={() => handleOpenPlayer(player)}
                            className="text-left"
                          >
                            <div className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline">
                              {displayName}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {player.mobile_number}
                            </div>
                          </button>
                        </td>

                        <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                          {player.last_login
                            ? new Date(player.last_login).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })
                            : 'Never'}
                        </td>

                        <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                          {player.mobile_number || '—'}
                        </td>

                        <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                          {player.first_name || '—'}
                        </td>

                        <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold text-slate-900">
                          {formatCurrency(player.points)}
                        </td>

                        <td className="whitespace-nowrap px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              player.is_active
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-red-50 text-red-700'
                            }`}
                          >
                            {player.status_display ||
                              (player.is_active ? 'Active' : 'Inactive')}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleOpenPlayer(player)}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                          >
                            <Eye className="h-4 w-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-500">
              Page{' '}
              <span className="font-medium text-slate-700">{currentPage}</span>{' '}
              of <span className="font-medium text-slate-700">{totalPages}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((page) => Math.max(1, page - 1))
                }
                disabled={
                  loading ||
                  currentPage <= 1 ||
                  pagination?.has_previous === false
                }
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>

              <button
                type="button"
                onClick={() => setCurrentPage((page) => page + 1)}
                disabled={
                  loading ||
                  currentPage >= totalPages ||
                  pagination?.has_next === false
                }
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayersPage;
